import React, { useEffect } from 'react'
import { Domains } from '@ally/data-types-host'
import { LoadState } from '@ally/use-data-domain'
import { HostServices } from '@ally/federated-types'
import { Router as ReactRouter } from 'react-router-dom'
import { muiBankTheme, MetronomeProvider } from '@ally/metronome-ui'
import ErrorBoundary from '@ally/next-remote-error-boundary'
import usePortalHook from '@ally/use-portal'
import { HostServicesContext } from './HostServices'
import Router from './routes'
import { AccountTypeProvider } from './AccountTypeContext'
import { CustomizeDashboardProvider } from './CustomizeDashboard/CustomizeDashboardContext'
import RouteSkeletonLoader from './Shared/Loader/RouteSkeletonLoader'
import { DebtPlanFocusAccountContext } from './DebtPlan/DebtPlanFocusAccountContext'
import useGetDebtPlanFocusAccount from './DebtPlan/useGetDebtPlanFocusAccount'

interface AppProps {
  services: HostServices
}

const App: React.FC<AppProps> = ({ services }) => {
  const name = 'dashboard'
  const { AboveAppPortal, useTogglePortalRoot } = usePortalHook()

  useTogglePortalRoot()

  const { browserHistory, hostData, session, featureFlags } = services
  const loadedStates = [LoadState.LOADED, LoadState.FAILED]
  const accountsLoaded = loadedStates.includes(
    hostData[Domains.AFG_ACCOUNTS].status.loadState,
  )

  const isCustomizationEnabled = featureFlags.variation(
    'FF_snapshot-customization',
    true,
  )

  // allows for updating customization without rendering skeleton loader
  const { isUpdating } = hostData[Domains.CUSTOMIZATION].status

  const customizationLoaded = !isCustomizationEnabled
    ? true
    : loadedStates.includes(hostData[Domains.CUSTOMIZATION].status.loadState)

  const focusAccountState = useGetDebtPlanFocusAccount(services)

  const isLoaded = accountsLoaded && customizationLoaded

  useEffect(() => session.requireAuth(), [session])

  return (
    <ReactRouter history={browserHistory}>
      <MetronomeProvider theme={muiBankTheme}>
        <HostServicesContext.Provider value={services}>
          <ErrorBoundary hostServices={services} name={name}>
            {!isLoaded && !isUpdating ? (
              <RouteSkeletonLoader />
            ) : (
              <AccountTypeProvider>
                <CustomizeDashboardProvider>
                  <DebtPlanFocusAccountContext.Provider
                    value={focusAccountState}
                  >
                    <Router AboveAppPortal={AboveAppPortal} />
                  </DebtPlanFocusAccountContext.Provider>
                </CustomizeDashboardProvider>
              </AccountTypeProvider>
            )}
          </ErrorBoundary>
        </HostServicesContext.Provider>
      </MetronomeProvider>
    </ReactRouter>
  )
}

export default App
